import {
  type Activatable,
  type ActivationLifecycle,
  type Deactivatable,
} from '@/infra/activation'
import { action, observable } from 'mobx'
import { toStream } from '@/utils/mobx-util'
import { isObservable, type Observable, type Subscription } from 'rxjs'
import { createAbortError } from '@/utils/abort-util'

/**
 * Lifecycle tracker which tracks activation and deactivation.
 * Exposes an {@link AbortSignal} (@{see signal}) which will be
 * in the aborted state while deactivated. Also provides utilities
 * for managing Rx subscriptions.
 */
export class LifecycleTracker implements ActivationLifecycle {
  @observable private accessor _active = false

  private _activationAbortController: AbortController | null = null
  private _activationAbortSignal: AbortSignal | null = null

  private _observables: Observable<unknown>[] = []
  private _activatables: (Activatable | Deactivatable)[] = []

  private _observableSubscriptions: {
    subscription: Subscription
    observable: Observable<unknown>
  }[] = []

  /**
   * Emits a {@link boolean} indicating whether the view model is active.
   */
  readonly active$ = toStream(() => this._active, true)

  /**
   * Indicates whether the tracker is currently in the active state.
   */
  get active() {
    // NOTE: We're not using `@computed` since this is a basic getter.
    return this._active
  }

  /**
   * An {@link AbortSignal} that will be aborted once the lifecycle tracker
   * enters the deactivated state.
   */
  get signal(): AbortSignal {
    return (
      this._activationAbortSignal ??
      AbortSignal.abort(
        createAbortError('LifecycleTracker has not been activated yet'),
      )
    )
  }

  /**
   * Activates the view model.
   */
  @action.bound
  activate(): this {
    // Don't activate twice.
    if (this._active) {
      return this
    }

    this._activationAbortController = new AbortController()
    this._activationAbortSignal = this._activationAbortController.signal
    this._active = true

    for (const observable of this._observables) {
      this._observableSubscriptions.push({
        observable,
        subscription: observable.subscribe(),
      })
    }

    for (const activatable of this._activatables) {
      if ('activate' in activatable) {
        activatable.activate()
      }
    }

    return this
  }

  /**
   * Subscribes to the observable while the tracker is active,
   * unsubscribes when no longer active.
   *
   * @param observable
   */
  manage(observable: Observable<unknown>): this
  /**
   * Activates and deactivates the activatable with this lifecycle.
   *
   * @param activatable
   */
  manage(activatable: Activatable | Deactivatable): this
  @action.bound
  manage(
    observableOrActivatable: Observable<unknown> | Activatable | Deactivatable,
  ): this {
    if ('activate' in observableOrActivatable) {
      if (this._activatables.includes(observableOrActivatable)) {
        return this
      }

      this._activatables.push(observableOrActivatable)

      if (this.active) {
        observableOrActivatable.activate()
      }

      return this
    }

    if (isObservable(observableOrActivatable)) {
      if (this._observables.includes(observableOrActivatable)) {
        return this
      }

      this._observables.push(observableOrActivatable)

      // If we're already active, subscribe immediately.
      if (this._active && this._observableSubscriptions) {
        this._observableSubscriptions.push({
          observable: observableOrActivatable,
          subscription: observableOrActivatable.subscribe(),
        })
      }
    }

    return this
  }

  /**
   * Deactivates and removes the activatable from the tracker.
   * Will only deactivate if currently tracked and active.
   *
   * @param observableOrActivatable
   */
  @action.bound
  remove(
    observableOrActivatable: Observable<unknown> | Activatable | Deactivatable,
  ): this {
    if ('subscribe' in observableOrActivatable) {
      // Remove the observable and it's subscription.
      const idx = this._observables.indexOf(observableOrActivatable)
      if (idx === -1) {
        return this
      }

      // This is safe because the observables will be subscribed in the order
      // they are added, so the index will be the same.
      this._observables.splice(idx, 1)
      this._observableSubscriptions.splice(idx, 1)[0].subscription.unsubscribe()
      return this
    }

    const idx = this._activatables.indexOf(observableOrActivatable)
    if (idx === -1) {
      return this
    }

    this._activatables.splice(idx, 1)

    if (this.active) {
      if ('deactivate' in observableOrActivatable) {
        observableOrActivatable.deactivate()
      }
    }

    return this
  }

  /**
   * Deactivates the view model.
   */
  @action.bound
  deactivate(): this {
    if (!this._active) {
      // We're not currently active; do nothing.
      return this
    }

    // SAFETY: We know that in order to get to this point, instances
    // of the following nullable properties would be set.
    this._activationAbortController!.abort(
      createAbortError('Lifecycle tracker was deactivated'),
    )
    this._activationAbortController = null
    this._activationAbortSignal = null

    // Unsubscribe from observables in reverse order.
    while (this._observableSubscriptions.length) {
      this._observableSubscriptions.pop()!.subscription.unsubscribe()
    }

    // Deactivate in reverse order.
    for (let i = this._activatables.length - 1; i >= 0; i--) {
      const activatable = this._activatables[i]
      if ('deactivate' in activatable) {
        activatable.deactivate()
      }
    }

    this._active = false
    return this
  }
}
